﻿import {isNullOrEmpty, NamedValue, translation} from "@2d-front-monorepo/core";
import {
	initializeMvvmContext,
	MvvmDataProvider,
	MvvmDataProviderRef,
	MvvmSelect
} from "@2d-front-monorepo/design-library";
import {DriverResult, RequiredDriverProperties} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";
import {JSX, RefObject, useMemo} from "react";
import {Attachments} from "../Attachments";
import AttachmentType from "../enums/AttachmentType";
import {DriverComponent} from "../index";
import {DriverId} from "../nominalTypes/DriverId";
import {ComponentParts, ComponentWithChildrenParts} from "./ComponentParts";
import CreateAttachmentsComponent from "./CreateAttachmentsComponent";
import CreateDriverComponent, {CreateDriverModel} from "./CreateDriverComponent";
import PermitsMvvmErrorCollector from "./PermitsMvvmErrorCollector";

export interface CreateOrSelectDriverComponentProps extends ComponentParts {
	autoFocus?: boolean;
	contextRef?: RefObject<MvvmDataProviderRef<CreateOrSelectDriverModel>>;
	createEnabled?: boolean;
	createParts?: ComponentWithChildrenParts;
	drivers?: DriverResult[];
	filesPrefix: string;
	hiddenProperties?: RequiredDriverProperties;
	initialState: CreateOrSelectDriverModel;
	onSubmit: (formData: CreateOrSelectDriverModel) => Promise<void>;
	readOnlyProperties?: RequiredDriverProperties;
	requiredAttachments: AttachmentType[];
	requiredProperties: RequiredDriverProperties;
	selectParts?: ComponentWithChildrenParts;
}

export type CreateOrSelectDriverModel = {
	driverId: DriverId;
} & CreateDriverModel;

function createSelectItems(drivers: DriverResult[] | undefined, createEnabled: boolean | undefined): NamedValue<DriverId>[] {
	const result: NamedValue<DriverId>[] = [];
	if (drivers) {
		result.push(...drivers.map(r => {
			return {value: r.id, label: r.fullName}
		}));
	}

	if (createEnabled) {
		result.push({
			label: translation(PermitTranslations.add_new),
			value: "create"
		});
	}

	return result;
}

export default function CreateOrSelectDriverComponent(props: CreateOrSelectDriverComponentProps): JSX.Element {
	const {
		initialState,
		onSubmit,
		filesPrefix,
		drivers,
		contextRef,
		header,
		footer,
		requiredProperties,
		readOnlyProperties,
		hiddenProperties,
		requiredAttachments,
		createEnabled,
		createParts,
		selectParts
	} = props;

	const choose = useMemo(() => createSelectItems(drivers, createEnabled), [drivers, createEnabled]);
	const multiple = choose.length > 1;

	const context = initializeMvvmContext({
		initialState: () => {
			initialState.driverId = isNullOrEmpty(initialState.driverId) ? choose.length > 0 ? choose[0].value : "create" : initialState.driverId;
			return initialState;
		},
		contextRef: contextRef
	});

	const state = context.state;
	const selectedDriver = useMemo(() => drivers?.find(r => r.id === state.driverId), [state.driverId]);
	const expiredAttachments = useMemo(() => Attachments.findExpiredTypes(selectedDriver, requiredAttachments), [selectedDriver, requiredAttachments]);
	const replaceableAttachments = useMemo(() => Attachments.findSubmitted(selectedDriver), [selectedDriver]);
	const createVisible = createEnabled && state.driverId === "create";

	return <MvvmDataProvider<CreateOrSelectDriverModel> onSubmit={onSubmit} context={context}>
		<PermitsMvvmErrorCollector/>
		{header}
		{multiple && <MvvmSelect context={context}
                                 property="driverId"
                                 label={translation(PermitTranslations.new_driver__choose)}
                                 items={choose}/>}

		{selectedDriver && <div className="layout vertical gap">
			{selectParts?.header}
            <DriverComponent driver={selectedDriver} filesPrefix={filesPrefix}/>
			{selectParts?.children}
            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__required)}
                                        types={expiredAttachments}
                                        required={true}/>

            <CreateAttachmentsComponent context={context}
                                        label={translation(PermitTranslations.documentation__update)}
                                        types={replaceableAttachments}
                                        required={false}/>
			{selectParts?.footer}
        </div>}

		{createVisible &&
            <CreateDriverComponent context={context}
                                   requiredAttachments={requiredAttachments}
                                   requiredProperties={requiredProperties}
                                   readOnlyProperties={readOnlyProperties}
                                   hiddenProperties={hiddenProperties}
                                   header={createParts?.header}
                                   children={createParts?.children}
                                   footer={createParts?.footer}/>
		}
		{footer}
	</MvvmDataProvider>;
}
